import { Box, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

import { CARD_SX, DASH } from '../tokens';
import { DashedNote } from './DashedNote';

export interface ReclassificationThresholds {
  composite: number;
  speaking: number;
}

const HORIZONS = ['This year', '1-2 years', 'beyond 2 years', 'ltel risk'];
const SEGMENT_WEIGHTS = [153, 210, 295, 160];

const UNLOCK_NOTE =
  "Exit projections begin once your state's updated WIDA ACCESS cut scores are finalized and you have a second year of comparable ACCESS data from the '26-'27 school year.";

function ComingSoonChip() {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px 4px 4px',
        borderRadius: '4px',
        bgcolor: DASH.trackBg,
      }}
    >
      <LockIcon sx={{ fontSize: 16, color: DASH.lockedText }} />
      <Typography
        component="span"
        sx={{ fontWeight: 700, fontSize: '12px', lineHeight: '16px', color: DASH.lockedText }}
      >
        Coming Soon
      </Typography>
    </Box>
  );
}

export function ReclassificationPipeline({
  thresholds,
  variant = 'elevated',
}: {
  thresholds?: ReclassificationThresholds;
  variant?: 'elevated' | 'outlined';
}) {
  const containerSx =
    variant === 'outlined'
      ? { ...CARD_SX, boxShadow: 'none', border: `1px solid ${DASH.rowBorder}` }
      : CARD_SX;

  return (
    <Box sx={{ ...containerSx, display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography
            component="div"
            sx={{ fontWeight: 600, fontSize: '24px', lineHeight: '32px', color: DASH.ink }}
          >
            Exit Pipeline
          </Typography>
          <ComingSoonChip />
        </Box>
        {thresholds ? (
          <Typography
            component="div"
            sx={{ fontWeight: 500, fontSize: '16px', lineHeight: '24px', color: DASH.muted }}
          >
            Thresholds: {thresholds.composite} composite, {thresholds.speaking} speaking
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Box
          sx={{
            display: 'flex',
            gap: '4px',
            padding: '4px',
            borderRadius: '8px',
            bgcolor: DASH.trackBg,
          }}
        >
          {SEGMENT_WEIGHTS.map((weight, index) => (
            <Box
              key={HORIZONS[index]}
              aria-hidden
              sx={{ flexGrow: weight, height: '24px', borderRadius: '4px', bgcolor: DASH.surface }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: '24px' }}>
          {HORIZONS.map((label) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Box
                aria-hidden
                sx={{ width: '12px', height: '12px', borderRadius: '4px', bgcolor: DASH.trackBg }}
              />
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: '12px',
                  lineHeight: '16px',
                  textTransform: 'uppercase',
                  color: DASH.muted,
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <DashedNote>{UNLOCK_NOTE}</DashedNote>
    </Box>
  );
}
